/**
 * Mock receipt payloads for deferred-origination (sender perspective).
 */

export type DeferredOriginationMockReceiptStatus =
  | "awaiting_claim"
  | "payment_bound"
  | "payout_bound"
  | "executable"
  | "funded"
  | "failed"
  | "expired"
  | "cancelled";

export interface DeferredOriginationMockReceipt {
  originationId: string;
  bindingStatus: DeferredOriginationMockReceiptStatus;
  claimUrl?: string;
  amount?: number;
  destinationCurrency: string;
  createdAt: string;
  operationId?: string;
  receiveAmount?: number;
  sendAmount?: number;
  totalDebit?: number;
  fee?: number;
  fundingFee?: number;
  transactionFee?: number;
  taxFee?: number;
  totalFee?: number;
  exchangeRate?: number;
  sourceCurrency?: string;
  recipientName?: string;
  status?: string;
  fundingMethod?: string;
  fundingStatus?: string;
  cancellation?: {
    allowed: boolean;
    mode?: "void" | "request";
    expiresAt?: string;
  };
  references?: Array<{
    type: string;
    value: string;
    label?: Record<string, string>;
    presentation: string;
  }>;
}

export const DEFAULT_DEFERRED_ORIGINATION_RECEIPT_ID = "dpr-gallery-funded";

export const deferredOriginationMockReceipts: Record<
  string,
  DeferredOriginationMockReceipt
> = {
  "dpr-gallery-funded": {
    originationId: "dpr-gallery-funded",
    bindingStatus: "funded",
    claimUrl: "https://pay.leap.example/claim/dpr-gallery-funded",
    amount: 1742,
    destinationCurrency: "MXN",
    createdAt: "2026-06-09T12:00:00.000Z",
    operationId: "op_gallery_deferred_1",
    sendAmount: 100,
    receiveAmount: 1742,
    totalDebit: 101,
    fee: 1,
    fundingFee: 1,
    transactionFee: 0,
    taxFee: 0,
    exchangeRate: 17.42,
    sourceCurrency: "USD",
    recipientName: "Juan Perez",
    status: "completed",
    references: [
      {
        type: "PAYOUT_CODE",
        value: "998877",
        presentation: "PROMINENT",
        label: { en: "Payout code", es: "Clave de cobro", fr: "Code de paiement" },
      },
    ],
  },
  "dpr-gallery-pending": {
    originationId: "dpr-gallery-pending",
    bindingStatus: "awaiting_claim",
    claimUrl: "https://pay.leap.example/claim/dpr-gallery-pending",
    amount: 1742,
    destinationCurrency: "MXN",
    createdAt: "2026-06-04T12:00:00.000Z",
    sendAmount: 100,
    totalDebit: 101,
    sourceCurrency: "USD",
    recipientName: "Maria Lopez",
  },
  "dpr-gallery-cash": {
    originationId: "dpr-gallery-cash",
    bindingStatus: "payout_bound",
    claimUrl: "https://pay.leap.example/claim/dpr-gallery-cash",
    amount: 1306.5,
    destinationCurrency: "MXN",
    createdAt: "2026-06-07T12:00:00.000Z",
    sendAmount: 75,
    receiveAmount: 1306.5,
    totalDebit: 75,
    fee: 0,
    fundingFee: 0,
    transactionFee: 0,
    taxFee: 0,
    exchangeRate: 17.42,
    sourceCurrency: "USD",
    recipientName: "Ana Ruiz",
    status: "pending",
    fundingMethod: "CASH_DEPOSIT",
    fundingStatus: "awaiting_funding",
    cancellation: {
      allowed: true,
      mode: "request",
      expiresAt: "2099-01-01T00:00:00.000Z",
    },
  },
};

export const resolveDeferredOriginationReceiptId = (
  paramId: string | undefined,
  pathname: string
): string => {
  if (paramId && deferredOriginationMockReceipts[paramId]) return paramId;
  const lastSegment = pathname.split("/").filter(Boolean).pop();
  if (lastSegment && deferredOriginationMockReceipts[lastSegment])
    return lastSegment;
  return DEFAULT_DEFERRED_ORIGINATION_RECEIPT_ID;
};

export const getDeferredOriginationMockReceipt = (
  paramId: string | undefined,
  pathname: string
): DeferredOriginationMockReceipt =>
  deferredOriginationMockReceipts[
    resolveDeferredOriginationReceiptId(paramId, pathname)
  ] ?? deferredOriginationMockReceipts[DEFAULT_DEFERRED_ORIGINATION_RECEIPT_ID];
